import React, { useState, useCallback, useContext } from 'react';

import { createUseStyles, Input, Textarea, Button, Advice } from '@Sage/hoi-poi-ui';
import { URL_REGEX } from '@Sage/ssm-utils';

import { a2aInfo } from 'lib/services/copilotAgents';
import { useWeb5Context } from 'lib/Web5Context';

import { CopilotContext } from '../../../Context';
import CopilotSidebarTabHeader from '../../components/CopilotSidebarTabHeader';
import styles from './styles';
const useStyles = createUseStyles(styles, 'CopilotSidebarAgentsRemoteForm');

const CopilotSidebarAgentsRemoteForm = ({ agent, onCancel, onSave }) => {
    const { t } = useWeb5Context();
    const classes = useStyles();
    const { refreshAgents } = useContext(CopilotContext);
    const [isLoading, setIsLoading] = useState(false);
    const [url, setUrl] = useState(agent?.url);
    const [info, setInfo] = useState(
        agent
            ? {
                  name: agent?.name,
                  description: agent?.description,
              }
            : undefined,
    );
    const [error, setError] = useState(null);

    const onInnerCancel = useCallback(() => {
        onCancel();
        setTimeout(() => {
            setUrl();
            setError();
            setInfo();
        });
    }, [onCancel]);

    const loadURL = useCallback(() => {
        if (url && !URL_REGEX.test(url)) {
            setError(t('label_copilot_agent_url_invalid'));
            setInfo();
        } else if (url) {
            setError(null);
            a2aInfo(url)
                .then(({ data }) => {
                    setInfo(data);
                })
                .catch((err) => {
                    console.error(err);
                    setError(t('label_copilot_agent_url_fetch_error'));
                });
        }
    }, [t, url]);

    const onInnerSave = useCallback(() => {
        setIsLoading(true);
        onSave({ url })
            .then(() => {
                onInnerCancel();
            })
            .finally(() => {
                setIsLoading(false);
                refreshAgents();
            });
    }, [onInnerCancel, onSave, refreshAgents, url]);

    return (
        <div className={classes.root}>
            <CopilotSidebarTabHeader
                title={t(agent?.id ? 'label_copilot_edit_agent' : 'label_copilot_create_agent')}
                onBack={onInnerCancel}
                sideComponent={
                    <Button
                        type="primary"
                        onClick={onInnerSave}
                        isLoading={isLoading}
                        isDisabled={!url}
                    >
                        {t('action_save')}
                    </Button>
                }
            />
            <Advice
                type="semanticInfo"
                title={t('label_copilot_agent_advice_title')}
                showIcon
                defaultCollapsed={false}
            >
                {t('label_copilot_agent_remote_advice_body')}
            </Advice>
            <Input
                label={t('label_copilot_agent_url_label')}
                placeholder={t('placeholder_copilot_agent_url')}
                value={url}
                onChange={setUrl}
                onEnter={loadURL}
                onBlur={loadURL}
                error={error}
                isFullWidth
                isRequired
            />
            {info && (
                <>
                    <Input label={t('label_name')} value={info.name} isFullWidth isReadOnly />
                    <Textarea
                        label={t('label_description')}
                        value={info.description}
                        minRows={2}
                        maxRows={4}
                        isResizable={false}
                        isFullWidth
                        isReadOnly
                    />
                </>
            )}
        </div>
    );
};

export default CopilotSidebarAgentsRemoteForm;
